import { type RawAxiosRequestHeaders } from "axios";
import { ApiConfig, ApiResult } from "common/models";
import { EngBaseHTTPClient } from "./eng-base-http-client.service";
import { msalInstance } from "common/config/auth.config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export abstract class EngHttpClient extends EngBaseHTTPClient {
  protected static async invokeApi<T>(
    apiConfig: ApiConfig,
    headers: RawAxiosRequestHeaders = {}
  ): Promise<ApiResult<T>> {
    const request = {
      scopes: [process.env.REACT_APP_API_SCOPE || ""]
    };
    let tokenResult;
    try {
      tokenResult = await msalInstance.acquireTokenSilent(request);
    } catch (error: any) {
      if (error instanceof InteractionRequiredAuthError) {
        // Prompt the user for consent interactively
        tokenResult = await msalInstance.acquireTokenPopup(request);
      } else {
        throw error;
      }
    }
    headers.Authorization = `Bearer ${tokenResult.accessToken}`;

    let result!: ApiResult<T>;
    switch (apiConfig.method) {
      case "GET": {
        const url =
          apiConfig.url +
          (apiConfig.queryString ? `?${apiConfig.queryString}` : "");
        result = await this.get<T>(url, headers);
        break;
      }
      case "POST":
        {
          const url =
            apiConfig.url +
            (apiConfig.queryString ? `?${apiConfig.queryString}` : "");
          result = await this.post<T>(url, apiConfig.data, headers);
        }
        break;
      case "PUT":
        result = await this.put<T>(apiConfig.url, apiConfig.data, headers);
        break;
      case "DELETE": {
        result = await this.delete<T>(apiConfig.url, apiConfig.data, headers);
        break;
      }
    }
    return result;
  }

  protected static async invokeApiWithTimeout<T>(
    apiConfig: ApiConfig,
    headers: RawAxiosRequestHeaders = {},
    timeout = 900000 // 15 minutes default
  ): Promise<ApiResult<T>> {
    const request = {
      scopes: [process.env.REACT_APP_API_SCOPE || ""]
    };
    let tokenResult;
    try {
      tokenResult = await msalInstance.acquireTokenSilent(request);
    } catch (error: any) {
      if (error instanceof InteractionRequiredAuthError) {
        tokenResult = await msalInstance.acquireTokenPopup(request);
      } else {
        throw error;
      }
    }
    headers.Authorization = `Bearer ${tokenResult.accessToken}`;

    let result!: ApiResult<T>;
    switch (apiConfig.method) {
      case "GET": {
        const url =
          apiConfig.url +
          (apiConfig.queryString ? `?${apiConfig.queryString}` : "");
        result = await this.getWithTimeout<T>(url, headers, timeout);
        break;
      }
      case "POST":
        {
          const url =
            apiConfig.url +
            (apiConfig.queryString ? `?${apiConfig.queryString}` : "");
          result = await this.postWithTimeout<T>(
            url,
            apiConfig.data,
            headers,
            timeout
          );
        }
        break;
      case "PUT":
        result = await this.putWithTimeout<T>(
          apiConfig.url,
          apiConfig.data,
          headers,
          timeout
        );
        break;
      case "DELETE": {
        result = await this.deleteWithTimeout<T>(
          apiConfig.url,
          apiConfig.data,
          headers,
          timeout
        );
        break;
      }
    }
    return result;
  }
}
